import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Download,
  Edit2,
  ExternalLink,
  Image,
  Loader2,
  Package,
  Play,
  RefreshCw,
  Search,
  Upload,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusBadgeClasses = {
  new: "bg-green-100 text-green-800",
  duplicate: "bg-amber-100 text-amber-800",
  published: "bg-blue-100 text-blue-800",
};

export const TadImportManager = () => {
  // Job state
  const [currentJob, setCurrentJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [isStarting, setIsStarting] = useState(false);

  // Preview state
  const [previewRows, setPreviewRows] = useState([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewLimit, setPreviewLimit] = useState(25);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});

  // Edit dialog
  const [editDialog, setEditDialog] = useState(null);
  const [editValue, setEditValue] = useState("");

  // Publish state
  const [isPublishing, setIsPublishing] = useState(false);
  const [duplicateAction, setDuplicateAction] = useState("ask");
  const [duplicateDecisions, setDuplicateDecisions] = useState({});
  const [needsDecisionRows, setNeedsDecisionRows] = useState([]);
  const [publishResult, setPublishResult] = useState(null);

  // Fetch jobs list
  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/tad-import/jobs`);
      setJobs(res.data.jobs || []);
    } catch (error) {
      console.error("Failed to fetch TAD jobs:", error);
    }
  }, []);

  // Poll job status
  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const res = await axios.get(`${API}/admin/tad-import/status/${jobId}`);
      setCurrentJob(res.data);
      return res.data;
    } catch (error) {
      console.error("Failed to poll job status:", error);
      return null;
    }
  }, []);

  // Fetch preview data
  const fetchPreview = useCallback(async (jobId, page = 1) => {
    try {
      const params = {
        page,
        limit: previewLimit,
        ...(statusFilter && { status_filter: statusFilter }),
        ...(searchQuery && { search: searchQuery }),
      };
      const res = await axios.get(`${API}/admin/tad-import/preview/${jobId}`, { params });
      setPreviewRows(res.data.rows || []);
      setPreviewTotal(res.data.total || 0);
      setPreviewPage(res.data.page || 1);
    } catch (error) {
      console.error("Failed to fetch preview:", error);
    }
  }, [previewLimit, statusFilter, searchQuery]);

  // Start new import job
  const startImport = async () => {
    setIsStarting(true);
    try {
      const res = await axios.post(`${API}/admin/tad-import/start`, {
        max_products: 0, // All products
      });
      toast.success("TAD Import started");
      setCurrentJob({ job_id: res.data.job_id, status: "starting" });
      setSelectedRowIds([]);
      setPublishResult(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start import");
    }
    setIsStarting(false);
  };

  // Edit product field
  const handleEdit = async () => {
    if (!editDialog || !currentJob) return;
    
    try {
      await axios.post(`${API}/admin/tad-import/edit`, {
        row_id: editDialog.rowId,
        field: editDialog.field,
        value: editValue,
      });
      toast.success(`Updated ${editDialog.field}`);
      setEditDialog(null);
      fetchPreview(currentJob.job_id, previewPage);
    } catch (error) {
      toast.error("Failed to update field");
    }
  };

  // Publish selected products
  const handlePublish = async () => {
    if (!currentJob || selectedRowIds.length === 0) return;
    
    setIsPublishing(true);
    try {
      const res = await axios.post(`${API}/admin/tad-import/publish`, {
        job_id: currentJob.job_id,
        selected_row_ids: selectedRowIds,
        duplicate_action: duplicateAction,
        duplicate_decisions: duplicateDecisions,
      });
      
      setPublishResult(res.data);
      
      if (res.data.needs_decision && res.data.needs_decision.length > 0) {
        setNeedsDecisionRows(res.data.needs_decision);
        toast.warning(`${res.data.needs_decision.length} products need duplicate decision`);
      } else {
        toast.success(`Created: ${res.data.created}, Updated: ${res.data.updated}, Skipped: ${res.data.skipped}`);
        fetchPreview(currentJob.job_id, previewPage);
        setSelectedRowIds([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Publish failed");
    }
    setIsPublishing(false);
  };

  // Make duplicate decision
  const handleDuplicateDecision = (rowId, decision) => {
    setDuplicateDecisions(prev => ({ ...prev, [rowId]: decision }));
    setNeedsDecisionRows(prev => prev.filter(r => r.row_id !== rowId));
  };

  // Toggle row selection
  const toggleRowSelection = (rowId) => {
    setSelectedRowIds(prev =>
      prev.includes(rowId)
        ? prev.filter(id => id !== rowId)
        : [...prev, rowId]
    );
  };

  // Toggle all visible rows
  const toggleAllRows = () => {
    if (selectedRowIds.length === previewRows.length) {
      setSelectedRowIds([]);
    } else {
      setSelectedRowIds(previewRows.map(r => r.row_id));
    }
  };

  // Toggle row expansion
  const toggleRowExpand = (rowId) => {
    setExpandedRows(prev => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  // Polling effect
  useEffect(() => {
    if (!currentJob?.job_id) return;
    
    const status = currentJob.status;
    if (["preview_ready", "completed", "failed"].includes(status)) {
      if (status === "preview_ready" || status === "completed") {
        fetchPreview(currentJob.job_id, 1);
      }
      return;
    }
    
    const interval = setInterval(async () => {
      const job = await pollJobStatus(currentJob.job_id);
      if (job && ["preview_ready", "completed", "failed"].includes(job.status)) {
        if (job.status === "preview_ready" || job.status === "completed") {
          fetchPreview(job.job_id, 1);
          // Auto-select new products
          const preview = await axios.get(`${API}/admin/tad-import/preview/${job.job_id}`);
          const newRows = (preview.data.rows || []).filter(r => r.status === "new");
          setSelectedRowIds(newRows.map(r => r.row_id));
        }
        clearInterval(interval);
      }
    }, 2000);
    
    return () => clearInterval(interval);
  }, [currentJob?.job_id, currentJob?.status, pollJobStatus, fetchPreview]);

  // Initial load
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Refetch preview when filters change
  useEffect(() => {
    if (currentJob?.status === "preview_ready" || currentJob?.status === "completed") {
      fetchPreview(currentJob.job_id, 1);
    }
  }, [statusFilter, searchQuery, previewLimit]);

  const renderJobProgress = () => {
    if (!currentJob) return null;
    
    const { status, total_products, scraped_products, current_product, progress_percent, error } = currentJob;
    
    if (status === "failed") {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Import Failed</span>
          </div>
          <p className="text-red-600 mt-1">{error}</p>
        </div>
      );
    }
    
    if (["starting", "logging_in", "fetching_product_list", "scraping"].includes(status)) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span className="font-medium text-blue-800">
                {status === "logging_in" && "Logging into TAD..."}
                {status === "fetching_product_list" && "Fetching product list..."}
                {status === "scraping" && `Scraping products (${scraped_products}/${total_products})...`}
                {status === "starting" && "Starting import..."}
              </span>
            </div>
            <span className="text-blue-600 font-medium">{progress_percent || 0}%</span>
          </div>
          {total_products > 0 && (
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${progress_percent || 0}%` }}
              />
            </div>
          )}
          {current_product && (
            <p className="text-blue-600 text-sm mt-2 truncate">Current: {current_product}</p>
          )}
        </div>
      );
    }
    
    return null;
  };

  const renderPreviewTable = () => {
    if (!previewRows.length) {
      return (
        <div className="text-center py-8 text-gray-500">
          No products to display
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="p-2 border text-left w-8">
                <Checkbox
                  checked={selectedRowIds.length === previewRows.length && previewRows.length > 0}
                  onCheckedChange={toggleAllRows}
                />
              </th>
              <th className="p-2 border text-left w-8"></th>
              <th className="p-2 border text-left">Status</th>
              <th className="p-2 border text-left">Image</th>
              <th className="p-2 border text-left">Name</th>
              <th className="p-2 border text-left">SKU</th>
              <th className="p-2 border text-left">Collection</th>
              <th className="p-2 border text-left">Category</th>
              <th className="p-2 border text-left">Colour</th>
              <th className="p-2 border text-left">Cost (£)</th>
              <th className="p-2 border text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row) => (
              <React.Fragment key={row.row_id}>
                <tr className="hover:bg-gray-50">
                  <td className="p-2 border">
                    <Checkbox
                      checked={selectedRowIds.includes(row.row_id)}
                      onCheckedChange={() => toggleRowSelection(row.row_id)}
                    />
                  </td>
                  <td className="p-2 border">
                    <button onClick={() => toggleRowExpand(row.row_id)} className="p-1 hover:bg-gray-200 rounded">
                      {expandedRows[row.row_id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="p-2 border">
                    <Badge className={statusBadgeClasses[row.status] || "bg-gray-100"}>
                      {row.status}
                    </Badge>
                    {row.has_edits && <span className="ml-1 text-amber-500">*</span>}
                  </td>
                  <td className="p-2 border">
                    {row.images?.[0] ? (
                      <img src={row.images[0]} alt="" className="w-12 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                        <Image className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="p-2 border font-medium max-w-[200px] truncate" title={row.name}>
                    {row.name}
                  </td>
                  <td className="p-2 border font-mono text-xs">{row.sku}</td>
                  <td className="p-2 border">{row.collection_name}</td>
                  <td className="p-2 border">{row.category}</td>
                  <td className="p-2 border">{row.colour}</td>
                  <td className="p-2 border">
                    {row.cost_price ? `£${row.cost_price.toFixed(2)}` : "-"}
                  </td>
                  <td className="p-2 border">
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditDialog({ rowId: row.row_id, field: "name", currentValue: row.name });
                          setEditValue(row.name);
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {row.product_url && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(row.product_url, "_blank")}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedRows[row.row_id] && (
                  <tr key={`${row.row_id}-expanded`}>
                    <td colSpan={11} className="p-4 bg-gray-50 border">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-600">Dimensions:</span>
                          <p>{row.size_cm || "-"}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Weight:</span>
                          <p>{row.weight || "-"}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">In Stock:</span>
                          <p>{row.in_stock ? "Yes" : "No"}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Images:</span>
                          <p>{row.images?.length || 0} images</p>
                        </div>
                        {row.status === "duplicate" && row.existing_sku && (
                          <div className="col-span-4">
                            <span className="font-medium text-amber-600">Existing Product SKU:</span>
                            <p className="text-amber-700">{row.existing_sku}</p>
                          </div>
                        )}
                      </div>
                      {row.images?.length > 0 && (
                        <div className="flex gap-2 mt-3 flex-wrap">
                          {row.images.slice(0, 6).map((img, idx) => (
                            <img key={idx} src={img} alt="" className="w-16 h-16 object-cover rounded border" />
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="tad-import-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">TAD Import</h2>
          <p className="text-gray-600">Import products from Timber Art Design UK</p>
        </div>
        <Button
          onClick={startImport}
          disabled={isStarting || (currentJob && !["preview_ready", "completed", "failed"].includes(currentJob.status))}
          className="bg-navy-600 hover:bg-navy-700"
          data-testid="start-tad-import-btn"
        >
          {isStarting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Play className="w-4 h-4 mr-2" />
          )}
          Start Import
        </Button>
      </div>

      {/* Progress */}
      {renderJobProgress()}

      {/* Summary Cards */}
      {currentJob && (currentJob.status === "preview_ready" || currentJob.status === "completed") && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Package className="w-4 h-4" />
              <span className="text-sm">Total Products</span>
            </div>
            <p className="text-2xl font-bold">{currentJob.total_products || 0}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">New Products</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{currentJob.new_count || 0}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">Duplicates</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{currentJob.duplicate_count || 0}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Check className="w-4 h-4" />
              <span className="text-sm">Selected</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{selectedRowIds.length}</p>
          </div>
        </div>
      )}

      {/* Publish Result */}
      {publishResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-medium text-green-800 mb-2">Publish Results</h4>
          <div className="flex gap-4 text-sm">
            <span className="text-green-700">Created: {publishResult.created}</span>
            <span className="text-blue-700">Updated: {publishResult.updated}</span>
            <span className="text-gray-600">Skipped: {publishResult.skipped}</span>
          </div>
        </div>
      )}

      {/* Duplicate Decisions */}
      {needsDecisionRows.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-medium text-amber-800 mb-3">Duplicate Products - Choose Action</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {needsDecisionRows.map((row) => (
              <div key={row.row_id} className="flex items-center justify-between bg-white p-2 rounded border">
                <div>
                  <p className="font-medium">{row.name}</p>
                  <p className="text-sm text-gray-600">SKU: {row.sku} → Existing: {row.existing_name}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDuplicateDecision(row.row_id, "skip")}
                  >
                    Skip
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDuplicateDecision(row.row_id, "update")}
                  >
                    Update Existing
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <Button
            className="mt-3"
            onClick={handlePublish}
            disabled={isPublishing || needsDecisionRows.length > 0}
          >
            Retry Publish
          </Button>
        </div>
      )}

      {/* Preview Section */}
      {currentJob && (currentJob.status === "preview_ready" || currentJob.status === "completed") && (
        <div className="bg-white rounded-lg border">
          {/* Filters */}
          <div className="p-4 border-b flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-xs"
              />
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New Only</SelectItem>
                <SelectItem value="duplicate">Duplicates Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={duplicateAction} onValueChange={setDuplicateAction}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Duplicate Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ask">Ask per product</SelectItem>
                <SelectItem value="skip">Skip all duplicates</SelectItem>
                <SelectItem value="update">Update all duplicates</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={handlePublish}
              disabled={isPublishing || selectedRowIds.length === 0}
              className="bg-green-600 hover:bg-green-700"
              data-testid="publish-tad-products-btn"
            >
              {isPublishing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Publish {selectedRowIds.length} Products
            </Button>
          </div>

          {/* Table */}
          {renderPreviewTable()}

          {/* Pagination */}
          {previewTotal > previewLimit && (
            <div className="p-4 border-t flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Showing {((previewPage - 1) * previewLimit) + 1}-{Math.min(previewPage * previewLimit, previewTotal)} of {previewTotal}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={previewPage <= 1}
                  onClick={() => {
                    setPreviewPage(p => p - 1);
                    fetchPreview(currentJob.job_id, previewPage - 1);
                  }}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={previewPage * previewLimit >= previewTotal}
                  onClick={() => {
                    setPreviewPage(p => p + 1);
                    fetchPreview(currentJob.job_id, previewPage + 1);
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {editDialog?.field}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 mb-4">Modify the value below and click Save to apply changes.</p>
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            placeholder={`Enter ${editDialog?.field}`}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>
              Cancel
            </Button>
            <Button onClick={handleEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recent Jobs */}
      {!currentJob && jobs.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-medium mb-3">Recent Import Jobs</h3>
          <div className="space-y-2">
            {jobs.slice(0, 5).map((job) => (
              <div
                key={job.job_id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  setCurrentJob(job);
                  if (job.status === "preview_ready" || job.status === "completed") {
                    fetchPreview(job.job_id, 1);
                  }
                }}
              >
                <div>
                  <span className="text-sm text-gray-600">{job.started_at}</span>
                  <Badge className={`ml-2 ${job.status === "completed" ? "bg-green-100" : "bg-gray-100"}`}>
                    {job.status}
                  </Badge>
                </div>
                <span className="text-sm">
                  {job.total_products} products ({job.new_count} new)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TadImportManager;
